import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import ListItemText from '@mui/material/ListItemText';
// routes
import { RouterLink } from 'src/routes/components';
//
import { useSelector, useDispatch } from 'react-redux';
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { openUpgradeDialog } from 'src/redux/slices/planSlice';
import PlanBadge from 'src/components/plan-badge/PlanBadge';
import Iconify from '../../iconify';
//
import { StyledItem, StyledIcon, StyledDotIcon } from './styles';

// ----------------------------------------------------------------------

export default function NavItem({ item, open, depth, active, config, externalLink, ...other }) {
  const { title, path, icon, info, children, disabled, caption, feature } = item;

  const dispatch = useDispatch();
  const { isLocked, requiredPlan, shouldShowBadge, type, value, badgePlan } = usePlanFeature(feature);

  const subItem = depth !== 1;
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const { drawerWidth, hasMounted } = useSelector((state) => state.drawer);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    requestAnimationFrame(checkTruncation);

    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMounted, drawerWidth, item]);

  const handleClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      dispatch(
        openUpgradeDialog({
          requiredPlan,
          featureLabel: title,
          featureType: type,
          limitValue: value,
        })
      );
    } else if (other.onClick) {
      other.onClick(e);
    }
  };

  const renderTitle = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, width: 1 }}>
      <span
        ref={textRef}
        style={{
          display: 'inline-block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}
      >
        {title}
      </span>
      {shouldShowBadge && (
        <PlanBadge
          plan={isLocked ? requiredPlan : badgePlan}
          isLocked={isLocked}
          variant="mini"
          sx={{ ml: 'auto' }}
        />
      )}
    </Box>
  );

  const renderContent = (
    <StyledItem
      disableGutters
      disabled={disabled}
      active={active}
      depth={depth}
      config={config}
      className={active ? 'active' : ''}
      sx={item.sx}
      {...other}
      onClick={handleClick}
    >
      <>
        {icon && (
          <Tooltip title={isTruncated ? title : null} arrow placement="top">
            <StyledIcon size={config.iconSize}>{icon}</StyledIcon>
          </Tooltip>
        )}

        {subItem && (
          <StyledIcon size={config.iconSize}>
            <StyledDotIcon active={active} />
          </StyledIcon>
        )}
      </>

      {!(config.hiddenLabel && !subItem) && (
        <ListItemText
          primary={
            isTruncated ? (
              <Tooltip title={title} arrow placement="top">
                {renderTitle}
              </Tooltip>
            ) : (
              renderTitle
            )
          }
          secondary={
            caption ? (
              <Tooltip title={caption} arrow placement="top">
                <span>{caption}</span>
              </Tooltip>
            ) : null
          }
          primaryTypographyProps={{
            noWrap: true,
            typography: 'body2',
            textTransform: 'capitalize',
            fontWeight: active ? 'fontWeightSemiBold' : 'fontWeightMedium',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            component: 'span',
            typography: 'caption',
            color: 'text.disabled',
          }}
        />
      )}

      {info && (
        <>
          <Box component="span" sx={{ ml: 1, lineHeight: 0 }}>
            {info}
          </Box>
          {!children && <Box width={16} sx={{ ml: 1, flexShrink: 0 }} />}
        </>
      )}

      {!!children && (
        <Iconify
          width={16}
          icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  // External link
  if (externalLink)
    return (
      <Link
        href={path}
        target="_blank"
        rel="noopener"
        underline="none"
        color="inherit"
        onClick={handleClick}
        sx={{
          ...(disabled && {
            cursor: 'default',
          }),
        }}
      >
        {renderContent}
      </Link>
    );

  // Has child
  if (children) {
    return renderContent;
  }

  // Default
  return (
    <Link
      component={RouterLink}
      href={path}
      underline="none"
      color="inherit"
      onClick={handleClick}
      sx={{
        ...(disabled && {
          cursor: 'default',
        }),
      }}
    >
      {renderContent}
    </Link>
  );
}

NavItem.propTypes = {
  active: PropTypes.bool,
  config: PropTypes.object,
  depth: PropTypes.number,
  externalLink: PropTypes.bool,
  item: PropTypes.object,
  open: PropTypes.bool,
};
